import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLang, T } from '../lib/lang';
import { YOUTUBE_VIDEO_ID } from '../content/video';

type Cue = {
  start: number; // seconds
  text: string;
};

function parseTimeToSeconds(ts: string) {
  // 00:01:23.456 or 01:23.456
  const parts = ts.trim().split(':');
  const [hh, mm, rest] =
    parts.length === 3 ? parts : parts.length === 2 ? ['0', parts[0], parts[1]] : ['0', '0', parts[0] ?? '0'];
  const [ssStr, msStr = '0'] = rest.split('.');
  const h = Number(hh);
  const m = Number(mm);
  const s = Number(ssStr);
  const ms = Number(msStr.padEnd(3, '0').slice(0, 3));
  return h * 3600 + m * 60 + s + ms / 1000;
}

function fmtTime(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (hh > 0) return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function cleanText(raw: string) {
  return raw
    .replace(/<[^>]+>/g, '') // remove <c>, <00:00:00.000>, etc.
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseVtt(vtt: string): Cue[] {
  const lines = vtt.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const cues: Cue[] = [];
  let i = 0;
  let lastText = '';
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line || line === 'WEBVTT' || line.startsWith('Kind:') || line.startsWith('Language:')) {
      i += 1;
      continue;
    }

    // time line like "00:00:05.400 --> 00:00:08.790 align:start position:0%"
    if (line.includes('-->')) {
      const startRaw = line.split('-->')[0]?.trim() ?? '0';
      const start = parseTimeToSeconds(startRaw);

      i += 1;
      const textLines: string[] = [];
      while (i < lines.length && lines[i].trim() !== '') {
        textLines.push(lines[i]);
        i += 1;
      }

      const text = cleanText(textLines.join(' '));
      if (text && text !== lastText) {
        cues.push({ start, text });
        lastText = text;
      }
      continue;
    }

    i += 1;
  }
  return cues;
}

export function TranscriptSection() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cues, setCues] = useState<Cue[] | null>(null);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    if (!cues) return [];
    const query = q.trim().toLowerCase();
    if (!query) return cues;
    return cues.filter((c) => c.text.toLowerCase().includes(query));
  }, [cues, q]);

  const load = async () => {
    if (cues) return;
    setLoading(true);
    try {
      const res = await fetch('/assets/vsRf07uC6DM.ru.vtt');
      const text = await res.text();
      setCues(parseVtt(text));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="transcript" className="py-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-dim">
            <T ru="transcript" en="transcript" />
          </div>
          <h2 className="mt-3 font-serif text-3xl font-light italic text-text md:text-4xl">
            <T ru="полная расшифровка" en="full transcript" />
          </h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-muted">
            <T
              ru="это автосубтитры YouTube (RU). можно искать по тексту и прыгать в оригинал по таймкоду."
              en="this is YouTube auto-captions (RU). you can search and jump to the original by timestamp."
            />
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="overflow-hidden rounded-xl border border-border bg-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-bg/40 px-5 py-4 backdrop-blur">
            <div className="text-[10px] uppercase tracking-widest text-muted">
              <T ru="auto captions" en="auto captions" /> · RU
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`https://youtu.be/${YOUTUBE_VIDEO_ID}`}
                target="_blank"
                rel="noreferrer"
                className="rounded border border-border px-3 py-1 text-[10px] uppercase tracking-widest text-text hover:border-gold hover:text-gold"
              >
                <T ru="youtube ↗" en="youtube ↗" />
              </a>
              <button
                type="button"
                onClick={async () => {
                  const next = !open;
                  setOpen(next);
                  if (next) await load();
                }}
                className="rounded bg-text px-3 py-1 text-[10px] uppercase tracking-widest text-bg hover:opacity-90"
              >
                {open ? <T ru="скрыть" en="hide" /> : <T ru="показать" en="show" />}
              </button>
            </div>
          </div>

          {open && (
            <div className="p-5">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={lang === 'ru' ? 'поиск по тексту…' : 'search…'}
                  className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-dim outline-none focus:border-gold sm:max-w-[420px]"
                />
                <div className="text-[12px] text-dim">
                  {loading ? (
                    <T ru="загрузка…" en="loading…" />
                  ) : cues ? (
                    <>
                      <span className="text-muted">{filtered.length}</span> / {cues.length}
                    </>
                  ) : null}
                </div>
              </div>

              <div className="max-h-[520px] overflow-auto">
                {filtered.map((c) => (
                  <div key={`${c.start}-${c.text}`} className="flex gap-4 border-t border-border px-2 py-3">
                    <a
                      href={`https://youtu.be/${YOUTUBE_VIDEO_ID}?t=${Math.floor(c.start)}s`}
                      target="_blank"
                      rel="noreferrer"
                      className="w-16 shrink-0 font-mono text-[11px] text-muted hover:text-gold"
                    >
                      {fmtTime(c.start)}
                    </a>
                    <div className="text-[12px] leading-6 text-text">{c.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}


